import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, RefreshCw, Settings, Palette } from "lucide-react";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import ImageTracer from "imagetracerjs";
import ToolsNav from "../components/ToolsNav";
import QuickConvert from "../components/QuickConvert";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

function ConvertPngToSvg({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preset, setPreset] = useState("default");
  const [colorCount, setColorCount] = useState([16]);
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const ACCEPT_TYPES = "image/png,image/jpeg,image/jpg,image/webp,image/bmp,image/gif,.png,.jpg,.jpeg,.webp,.bmp,.gif";

  // Tracing presets
  const presets = {
    default: { name: "Default", desc: "Balanced quality and detail" },
    posterized1: { name: "Posterized (Simple)", desc: "Simplified colors, smooth shapes" },
    posterized2: { name: "Posterized (Medium)", desc: "Medium detail level" },
    curvy: { name: "Curvy", desc: "Smooth curved lines" },
    sharp: { name: "Sharp", desc: "Sharp edges and corners" },
    detailed: { name: "Detailed", desc: "Maximum detail preservation" },
    smoothed: { name: "Smoothed", desc: "Extra smooth output" },
    grayscale: { name: "Grayscale", desc: "Black and white output" },
    fixedpalette: { name: "Fixed Palette", desc: "Web-safe color palette" },
    randomsampling1: { name: "Artistic 1", desc: "Artistic random sampling" },
    randomsampling2: { name: "Artistic 2", desc: "More artistic variation" },
  };

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/convert-png-to-svg`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          document.title = data.seo?.meta_title || "PNG to SVG Converter - KayTool";
        }
      } catch (error) {
        document.title = "PNG to SVG Converter - KayTool";
      }
    };
    
    if (!embedded) {
      fetchPageContent();
    }
  }, [embedded]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      processImages(files);
    } else {
      toast.error("Please drop image files only");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processImages(files);
    }
    e.target.value = '';
  };

  const traceImage = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      img.onload = () => {
        try {
          // Create canvas to get image data
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          
          // Configure tracing options
          let options = {};
          
          // Apply preset
          if (preset === 'posterized1') {
            options = { colorsampling: 0, numberofcolors: 4, mincolorratio: 0, colorquantcycles: 3 };
          } else if (preset === 'posterized2') {
            options = { colorsampling: 0, numberofcolors: 8, mincolorratio: 0, colorquantcycles: 3 };
          } else if (preset === 'curvy') {
            options = { ltres: 0.01, qtres: 0.01, pathomit: 8, rightangleenhance: false };
          } else if (preset === 'sharp') {
            options = { qtres: 0.01, ltres: 0.01, pathomit: 8, rightangleenhance: true, linefilter: true };
          } else if (preset === 'detailed') {
            options = { pathomit: 0, roundcoords: 2, ltres: 0.5, qtres: 0.5, numberofcolors: colorCount[0] };
          } else if (preset === 'smoothed') {
            options = { blurradius: 5, blurdelta: 64 };
          } else if (preset === 'grayscale') {
            options = { colorsampling: 0, colorquantcycles: 1, numberofcolors: 8 };
          } else if (preset === 'fixedpalette') {
            options = { colorsampling: 2, numberofcolors: 16 };
          } else if (preset === 'randomsampling1') {
            options = { colorsampling: 1, numberofcolors: 8 };
          } else if (preset === 'randomsampling2') {
            options = { colorsampling: 1, numberofcolors: 64 };
          } else {
            // Default
            options = { numberofcolors: colorCount[0] };
          }
          
          // Trace the image
          const svgString = ImageTracer.imagedataToSVG(imageData, options);
          resolve(svgString);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = imageUrl;
    });
  };

  const processImages = async (files) => {
    setIsProcessing(true);
    setProgress(0);

    const newImages = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress(Math.round(((i) / files.length) * 100));
      
      try {
        const imageUrl = URL.createObjectURL(file);
        const svgString = await traceImage(imageUrl);
        
        // Create SVG blob
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
        const svgFile = new File([svgBlob], file.name.replace(/\.[^/.]+$/, '.svg'), { type: 'image/svg+xml' });
        
        // Get original image dimensions
        const img = new Image();
        await new Promise((resolve) => {
          img.onload = resolve;
          img.src = imageUrl;
        });
        
        newImages.push({
          id: Date.now() + i,
          name: file.name,
          originalFile: file,
          originalSize: file.size,
          originalPreview: imageUrl,
          dimensions: { width: img.width, height: img.height },
          svgFile: svgFile,
          svgSize: svgBlob.size,
          svgPreview: URL.createObjectURL(svgBlob),
          svgString: svgString,
        });
      } catch (error) {
        console.error(`Error converting ${file.name}:`, error);
        toast.error(`Failed to convert ${file.name}`);
      }
    }

    setProgress(100);
    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    
    if (newImages.length > 0) {
      toast.success(`Converted ${newImages.length} image${newImages.length > 1 ? 's' : ''} to SVG!`);
    }
  };

  const removeImage = (id) => {
    setImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img) {
        URL.revokeObjectURL(img.originalPreview);
        URL.revokeObjectURL(img.svgPreview);
      }
      return prev.filter(i => i.id !== id);
    });
  };

  const downloadSingle = (image) => {
    saveAs(image.svgFile, image.svgFile.name);
  };

  const downloadAll = async () => {
    if (images.length === 0) return;
    
    if (images.length === 1) {
      downloadSingle(images[0]);
      return;
    }

    const zip = new JSZip();
    images.forEach((img) => {
      zip.file(img.svgFile.name, img.svgFile);
    });
    
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `png-to-svg-converted-${Date.now()}.zip`);
    toast.success("All SVGs downloaded!");
  };

  const clearAll = () => {
    images.forEach(img => {
      URL.revokeObjectURL(img.originalPreview);
      URL.revokeObjectURL(img.svgPreview);
    });
    setImages([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (embedded) {
    return (
      <div className="p-4">
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            isDragging ? 'border-violet-500 bg-violet-50' : 'border-slate-300 hover:border-violet-400'
          }`}
        >
          <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <p className="text-lg font-medium">Drop images here or click to upload</p>
          <p className="text-sm text-slate-500 mt-2">Convert raster images to vector SVG</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_TYPES}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
      
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KayTool
              </h1>
            </a>
            <div className="flex items-center gap-3">
              <QuickConvert />
            <ToolsNav />
              <Badge variant="secondary" className="hidden md:flex items-center gap-1 px-3 py-1 dark:bg-slate-700">
                <Info className="w-3 h-3" />
                Vector Tracing
              </Badge>
              <Button variant="outline" size="icon" onClick={() => setDarkMode(prev => !prev)} className="rounded-full">
                {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
            PNG to SVG
          </Badge>
          <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Convert PNG to SVG
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Transform raster images into scalable vector graphics using advanced image tracing. Perfect for logos, icons, and illustrations.
          </p>
        </div>

        {/* Settings */}
        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                <span className="font-medium text-slate-700 dark:text-slate-300">Tracing Style:</span>
              </div>
              <Select value={preset} onValueChange={setPreset} disabled={isProcessing}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(presets).map(([key, value]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex flex-col">
                        <span>{value.name}</span>
                        <span className="text-xs text-slate-500">{value.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {(preset === 'default' || preset === 'detailed') && (
                <div className="flex items-center gap-3">
                  <Palette className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Colors:</span>
                  <Slider
                    value={colorCount}
                    onValueChange={setColorCount}
                    min={2}
                    max={64}
                    step={1}
                    className="w-32"
                    disabled={isProcessing}
                  />
                  <Badge variant="secondary">{colorCount[0]}</Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <div
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                isProcessing ? 'opacity-50 cursor-not-allowed' :
                isDragging 
                  ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' 
                  : 'border-slate-300 dark:border-slate-600 hover:border-violet-400 dark:hover:border-violet-500'
              }`}
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <p className="text-xl font-medium text-slate-700 dark:text-slate-300">
                Drop images here or click to upload
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                Supports PNG, JPG, WebP, BMP, GIF
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_TYPES}
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={isProcessing}
            />
          </CardContent>
        </Card>

        {/* Progress */}
        {isProcessing && (
          <Card className="mb-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Tracing images to SVG...</span>
                <span className="text-sm text-slate-500">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {images.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Converted Images ({images.length})
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearAll} className="gap-2">
                  <RefreshCw className="w-4 h-4" /> Clear All
                </Button>
                <Button onClick={downloadAll} className="gap-2 bg-violet-600 hover:bg-violet-700">
                  <Download className="w-4 h-4" /> Download All
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden dark:bg-slate-800">
                  <CardContent className="p-0">
                    <div className="grid grid-cols-2 gap-0">
                      {/* Original */}
                      <div className="p-4 border-r border-slate-200 dark:border-slate-700">
                        <p className="text-xs font-medium text-slate-500 mb-2">Original (PNG)</p>
                        <div className="aspect-square bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZjFmNWY5Ii8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNmMWY1ZjkiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] rounded-lg overflow-hidden">
                          <img
                            src={image.originalPreview}
                            alt="Original"
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-2">{formatFileSize(image.originalSize)}</p>
                      </div>
                      
                      {/* SVG */}
                      <div className="p-4 relative">
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 w-6 h-6 z-10"
                          onClick={() => removeImage(image.id)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                        <p className="text-xs font-medium text-slate-500 mb-2">Vector (SVG)</p>
                        <div className="aspect-square bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZjFmNWY5Ii8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNmMWY1ZjkiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] rounded-lg overflow-hidden">
                          <img
                            src={image.svgPreview}
                            alt="SVG"
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-2">{formatFileSize(image.svgSize)}</p>
                      </div>
                    </div>
                    
                    <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium text-sm truncate dark:text-white">{image.name}</p>
                          <p className="text-xs text-slate-500">
                            {image.dimensions.width} × {image.dimensions.height}
                          </p>
                        </div>
                        <Button 
                          onClick={() => downloadSingle(image)} 
                          className="gap-2 bg-violet-600 hover:bg-violet-700"
                        >
                          <Download className="w-4 h-4" /> Download SVG
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { title: "True Vectorization", desc: "Actual image tracing, not just embedding - creates real vector paths" },
            { title: "Multiple Styles", desc: "Choose from 11 tracing presets for different results" },
            { title: "100% Private", desc: "All processing happens in your browser - images never leave your device" }
          ].map((feature, i) => (
            <Card key={i} className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <h4 className="font-semibold mb-2 dark:text-white">{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Ad Placement - Before Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content */}
      {pageContent?.content && (
        <div className="bg-white dark:bg-slate-800 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={pageContent.content} />
          </div>
        </div>
      )}


      {/* Ad Placement - Before Footer */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}

export default ConvertPngToSvg;
